/**
 * Sidebar variant for the "Architect" contour.
 *
 * Same chrome as the boards Sidebar (workspace header, collapse toggle,
 * resize handle) — the body lists recent Architect runs and exposes
 * "+ New task" + the empty "Cards from Architect" shortcut.
 */
import { useQuery } from '@tanstack/react-query';
import { Link, useMatchRoute } from '@tanstack/react-router';
import { ChevronsLeft, ChevronsUpDown, LogOut, Moon, Plus, Settings, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { authApi } from '../../lib/api';
import { architectApi, architectKeys } from '../../lib/api/architect';
import { useResizablePanel } from '../../lib/use-resizable-panel';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth-store';
import { useThemeStore } from '../../stores/theme-store';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

interface ArchitectSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function ArchitectSidebar({ collapsed, onToggle }: ArchitectSidebarProps) {
  const { t } = useTranslation();
  const matchRoute = useMatchRoute();
  const user = useAuthStore((s) => s.user);
  const workspaces = useAuthStore((s) => s.workspaces);
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const setUnauthenticated = useAuthStore((s) => s.setUnauthenticated);
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);

  const activeWorkspace = workspaces.find((w) => w.workspace.id === activeWorkspaceId)?.workspace;

  const { width, isDragging, onHandleMouseDown } = useResizablePanel({
    storageKey: 'chaos-sidebar-width',
    defaultWidth: 260,
    minWidth: 220,
    maxWidth: 380,
    side: 'left',
  });

  const runsQ = useQuery({
    queryKey: architectKeys.runs({ limit: 10 }),
    queryFn: () => architectApi.listRuns({ limit: 10 }),
  });

  const handleSignOut = async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch {
      /* ignore */
    }
    setUnauthenticated();
    window.location.href = '/login';
  };

  if (collapsed) return null;

  return (
    <aside
      aria-label="Architect navigation"
      style={{ width }}
      className={cn(
        'relative flex h-screen shrink-0 flex-col border-r border-border bg-muted/30',
        isDragging && 'select-none',
      )}
    >
      <div className="flex items-center gap-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="group flex flex-1 items-center justify-between px-3 py-2.5 text-left hover:bg-foreground/5"
              aria-label="Account menu"
            >
              <div className="flex min-w-0 flex-col leading-tight">
                <span className="truncate text-[13px] font-semibold tracking-tight text-foreground">
                  {activeWorkspace?.name ?? user?.displayName ?? user?.email ?? t('app.name')}
                </span>
                <span className="truncate text-[11px] text-muted-foreground">
                  {user?.email ?? ''}
                </span>
              </div>
              <ChevronsUpDown
                className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
                aria-hidden
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="bottom" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium">{user?.displayName ?? user?.email}</span>
                <span className="text-xs text-muted-foreground">{user?.email}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              <span>{theme === 'dark' ? t('nav.light_mode') : t('nav.dark_mode')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/settings/profile">
                <Settings className="h-4 w-4" />
                <span>{t('settings.profile.title')}</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut}>
              <LogOut className="h-4 w-4" />
              <span>{t('dashboard.sign_out')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <button
          type="button"
          onClick={onToggle}
          aria-label="Collapse sidebar"
          className="mr-1.5 flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
        >
          <ChevronsLeft className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 pb-2 pt-3">
        <div className="px-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/80">
          Architect
        </div>

        <ul className="mt-1 flex flex-col">
          <li>
            <Link
              to="/architect"
              className={cn(
                'flex items-center rounded px-2 py-1 text-[13px] transition-colors',
                matchRoute({ to: '/architect' })
                  ? 'bg-foreground/5 text-foreground'
                  : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
              )}
            >
              Home
            </Link>
          </li>
          <li>
            <Link
              to="/architect/dispatch"
              className={cn(
                'flex items-center gap-1.5 rounded px-2 py-1 text-[13px] transition-colors',
                matchRoute({ to: '/architect/dispatch' })
                  ? 'bg-foreground/5 text-foreground'
                  : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
              )}
            >
              <Plus className="h-3.5 w-3.5" aria-hidden />
              <span>New task</span>
            </Link>
          </li>
        </ul>

        <div className="mt-4 px-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/80">
          Recent runs
        </div>
        <ul className="mt-1 flex flex-col">
          {runsQ.isLoading && (
            <li className="px-2 py-1 text-xs text-muted-foreground">{t('common.loading')}</li>
          )}
          {runsQ.data?.runs.slice(0, 10).map((r) => {
            const isActive = !!matchRoute({
              to: '/architect/run/$runId',
              params: { runId: r.id },
              fuzzy: true,
            });
            return (
              <li key={r.id}>
                <Link
                  to="/architect/run/$runId"
                  params={{ runId: r.id }}
                  className={cn(
                    'flex items-center rounded px-2 py-1 text-[13px] transition-colors',
                    isActive
                      ? 'bg-foreground/5 text-foreground'
                      : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
                  )}
                  title={r.description}
                >
                  <span className="truncate">{r.description}</span>
                </Link>
              </li>
            );
          })}
          {runsQ.data && runsQ.data.runs.length === 0 && (
            <li className="px-2 py-1 text-xs text-muted-foreground/70">No runs yet.</li>
          )}
        </ul>
      </nav>

      <button
        type="button"
        aria-label="Resize sidebar"
        tabIndex={-1}
        onMouseDown={onHandleMouseDown}
        className={cn(
          'absolute right-0 top-0 z-10 h-full w-1 cursor-col-resize border-0 bg-transparent p-0',
          'transition-colors',
          isDragging ? 'bg-foreground/20' : 'hover:bg-foreground/10',
        )}
      />
    </aside>
  );
}
